/**
 * Public CMS Hooks
 *
 * TanStack Query hooks for fetching public CMS data.
 */
import { useQuery } from '@tanstack/react-query'
import type { PageData } from '../l2-editor/types'
import type { CmsCategory, CmsTag, MenuItem } from '@/server/modules/cms/db/schema'

const API_BASE = '/api/public/cms'

// ============================================================================
// Query Keys
// ============================================================================

export const PUBLIC_CMS_KEYS = {
  all: ['public-cms'] as const,
  pages: () => [...PUBLIC_CMS_KEYS.all, 'pages'] as const,
  page: (path: string) => [...PUBLIC_CMS_KEYS.pages(), path] as const,
  menus: () => [...PUBLIC_CMS_KEYS.all, 'menus'] as const,
  menu: (slug: string) => [...PUBLIC_CMS_KEYS.menus(), slug] as const,
  categories: () => [...PUBLIC_CMS_KEYS.all, 'categories'] as const,
  category: (slug: string) => [...PUBLIC_CMS_KEYS.categories(), slug] as const,
  categoryPosts: (slug: string, page: number) => [...PUBLIC_CMS_KEYS.category(slug), 'posts', page] as const,
  tags: () => [...PUBLIC_CMS_KEYS.all, 'tags'] as const,
  tag: (slug: string) => [...PUBLIC_CMS_KEYS.tags(), slug] as const,
  tagPosts: (slug: string, page: number) => [...PUBLIC_CMS_KEYS.tag(slug), 'posts', page] as const,
  blog: () => [...PUBLIC_CMS_KEYS.all, 'blog'] as const,
  blogList: (page: number) => [...PUBLIC_CMS_KEYS.blog(), 'list', page] as const,
}

// ============================================================================
// Types
// ============================================================================

interface PublicPageData {
  id: string
  title: string
  slug: string
  path: string
  type: string
  metaTitle: string | null
  metaDescription: string | null
  ogImage: string | null
  excerpt: string | null
  featuredImage: string | null
  publishedAt: string | null
  content: PageData | null
  category: CmsCategory | null
  tags: CmsTag[]
}

interface PublicMenuData {
  id: string
  name: string
  slug: string
  location: string | null
  items: MenuItem[]
}

// ============================================================================
// Page Hooks
// ============================================================================

/**
 * Fetch a published page by path
 */
export function usePublicPage(path: string) {
  return useQuery({
    queryKey: PUBLIC_CMS_KEYS.page(path),
    queryFn: async (): Promise<{ page: PublicPageData }> => {
      const response = await fetch(`${API_BASE}/pages${path}`)
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Page not found')
        }
        throw new Error('Failed to fetch page')
      }
      return response.json()
    },
    enabled: !!path,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: false, // Don't retry 404s
  })
}

// ============================================================================
// Menu Hooks
// ============================================================================

/**
 * Fetch a menu by slug
 */
export function usePublicMenu(slug: string) {
  return useQuery({
    queryKey: PUBLIC_CMS_KEYS.menu(slug),
    queryFn: async (): Promise<{ menu: PublicMenuData }> => {
      const response = await fetch(`${API_BASE}/menus/${slug}`)
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Menu not found')
        }
        throw new Error('Failed to fetch menu')
      }
      return response.json()
    },
    enabled: !!slug,
    staleTime: 10 * 60 * 1000, // Cache menus for 10 minutes
  })
}

// ============================================================================
// Category & Tag Hooks
// ============================================================================

/**
 * Fetch all categories
 */
export function usePublicCategories() {
  return useQuery({
    queryKey: PUBLIC_CMS_KEYS.categories(),
    queryFn: async (): Promise<{ categories: CmsCategory[] }> => {
      const response = await fetch(`${API_BASE}/categories`)
      if (!response.ok) throw new Error('Failed to fetch categories')
      return response.json()
    },
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
  })
}

/**
 * Fetch all tags
 */
export function usePublicTags() {
  return useQuery({
    queryKey: PUBLIC_CMS_KEYS.tags(),
    queryFn: async (): Promise<{ tags: CmsTag[] }> => {
      const response = await fetch(`${API_BASE}/tags`)
      if (!response.ok) throw new Error('Failed to fetch tags')
      return response.json()
    },
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
  })
}

// ============================================================================
// Blog Hooks
// ============================================================================

interface BlogPost {
  id: string
  title: string
  slug: string
  path: string
  excerpt: string | null
  featuredImage: string | null
  publishedAt: string | null
  metaTitle: string | null
  metaDescription: string | null
  category: {
    id: string
    name: string
    slug: string
    color: string | null
  } | null
  author: {
    id: string
    name: string
    image: string | null
  } | null
}

interface BlogListResponse {
  posts: BlogPost[]
  pagination: {
    total: number
    limit: number
    offset: number
    hasMore: boolean
  }
}

interface CategoryWithCount extends CmsCategory {
  postCount: number
}

interface TagWithCount extends CmsTag {
  postCount: number
}

const POSTS_PER_PAGE = 10

/**
 * Fetch blog posts with pagination
 */
export function useBlogPosts(page: number = 1) {
  const offset = (page - 1) * POSTS_PER_PAGE

  return useQuery({
    queryKey: PUBLIC_CMS_KEYS.blogList(page),
    queryFn: async (): Promise<BlogListResponse> => {
      const params = new URLSearchParams({
        limit: String(POSTS_PER_PAGE),
        offset: String(offset),
      })
      const response = await fetch(`${API_BASE}/blog?${params}`)
      if (!response.ok) throw new Error('Failed to fetch blog posts')
      return response.json()
    },
    staleTime: 2 * 60 * 1000, // Cache for 2 minutes
  })
}

/**
 * Fetch a category with post count
 */
export function usePublicCategory(slug: string) {
  return useQuery({
    queryKey: PUBLIC_CMS_KEYS.category(slug),
    queryFn: async (): Promise<{ category: CategoryWithCount }> => {
      const response = await fetch(`${API_BASE}/categories/${slug}`)
      if (!response.ok) {
        if (response.status === 404) throw new Error('Category not found')
        throw new Error('Failed to fetch category')
      }
      return response.json()
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
}

/**
 * Fetch blog posts by category
 */
export function useCategoryPosts(slug: string, page: number = 1) {
  const offset = (page - 1) * POSTS_PER_PAGE

  return useQuery({
    queryKey: PUBLIC_CMS_KEYS.categoryPosts(slug, page),
    queryFn: async (): Promise<BlogListResponse & { category: CmsCategory }> => {
      const params = new URLSearchParams({
        limit: String(POSTS_PER_PAGE),
        offset: String(offset),
      })
      const response = await fetch(`${API_BASE}/categories/${slug}/posts?${params}`)
      if (!response.ok) {
        if (response.status === 404) throw new Error('Category not found')
        throw new Error('Failed to fetch category posts')
      }
      return response.json()
    },
    enabled: !!slug,
    staleTime: 2 * 60 * 1000,
    retry: false,
  })
}

/**
 * Fetch a tag with post count
 */
export function usePublicTag(slug: string) {
  return useQuery({
    queryKey: PUBLIC_CMS_KEYS.tag(slug),
    queryFn: async (): Promise<{ tag: TagWithCount }> => {
      const response = await fetch(`${API_BASE}/tags/${slug}`)
      if (!response.ok) {
        if (response.status === 404) throw new Error('Tag not found')
        throw new Error('Failed to fetch tag')
      }
      return response.json()
    },
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
}

/**
 * Fetch blog posts by tag
 */
export function useTagPosts(slug: string, page: number = 1) {
  const offset = (page - 1) * POSTS_PER_PAGE

  return useQuery({
    queryKey: PUBLIC_CMS_KEYS.tagPosts(slug, page),
    queryFn: async (): Promise<BlogListResponse & { tag: CmsTag }> => {
      const params = new URLSearchParams({
        limit: String(POSTS_PER_PAGE),
        offset: String(offset),
      })
      const response = await fetch(`${API_BASE}/tags/${slug}/posts?${params}`)
      if (!response.ok) {
        if (response.status === 404) throw new Error('Tag not found')
        throw new Error('Failed to fetch tag posts')
      }
      return response.json()
    },
    enabled: !!slug,
    staleTime: 2 * 60 * 1000,
    retry: false,
  })
}
